/**
 * 브라우저의 뒤로가기를 누르면 **정말 나갈지 묻는다.** 플레이한 사람: "브라우져 뒤로가기를 누르면 확인창을 띄우자. 정말
 * 게임을 나갈지." 폰에서는 뒤로가기가 손짓 하나라 잘못 누르기 쉽고, 출정은 계정에 적히지 않아 나가면 그 출정을 버린다.
 *
 * 방법: 방문 기록에 문지기 항목을 하나 얹어 둔다. 뒤로가기는 그 항목만 걷어 내고 페이지에 머문다(`popstate`) — 그때
 * 확인 창을 띄운다. "계속하기"는 문지기를 다시 얹고, "나가기"는 한 번 더 뒤로 간다(이제는 진짜로 떠난다). 창이 뜬
 * 채로 뒤로가기를 또 누르면 문지기가 없으니 그대로 나간다 — 두 번 누르면 나간다는 폰의 버릇과 같다.
 *
 * - 문지기는 **첫 손짓 뒤에** 얹는다. 손대지 않은 페이지가 얹은 항목은 브라우저가 뒤로가기에서 건너뛴다(크롬의 기록
 *   조작 막기) — 불러오자마자 얹으면 있으나 마나고, 손짓 뒤에 또 얹으면 둘이 된다.
 * - `beforeunload`는 쓰지 않는다. 문구를 정할 수 없고, 새 판을 받으려고 스스로 다시 불러올 때(`reloadForNewVersion`)도 뜬다.
 */
import { button, el } from './dom.ts';

export interface LeaveHooks {
  /** 지금 무엇을 잃는가 — 창의 설명이 달라진다. */
  status(): 'battle' | 'run' | 'idle';
  /** 창이 떠 있는 동안 싸움을 세운다(싸우는 중일 때만 뜻이 있다). */
  hold(on: boolean): void;
  /** 창을 붙일 곳. 창의 색은 붙은 곳의 테마를 따른다 — 메뉴는 고른 밝기, 전장은 늘 어둡다. */
  host(): HTMLElement;
}

const GUARD = 'ildangbaek-guard';

function describe(status: 'battle' | 'run' | 'idle'): string {
  // 출정의 전과는 철수할 때에야 계정에 든다(`run.pendingMerit` → `retreat`). 처음에는 "이미 치른 판의 전과는 남아 있다"고
  // 적었는데 거짓이었다 — 철수하지 않고 나가면 앞 판의 전과도 함께 사라진다(리뷰가 잡았다).
  if (status === 'battle') return '싸우는 중이다. 나가면 이 출정은 여기서 끝나고, 이 출정에서 쌓은 전과는 남지 않는다.';
  if (status === 'run') return '출정 중이다. 철수하지 않고 나가면 이 출정에서 쌓은 전과는 남지 않는다.';
  return '지금까지의 부대와 군자금은 저장되어 있다.';
}

/** 나갈지 묻는 창이 떠 있는가. 문서에서 먼저(capture) 키를 받는 처리기가 비켜서는 데 쓴다(`fanfare.ts`). */
export function leaveAsking(): boolean {
  return document.querySelector('dialog.leave-dialog') !== null;
}

export function installLeaveGuard(hooks: LeaveHooks): void {
  // 다시 불러온 페이지는 문지기 항목 위에서 깨어난다(기록의 상태는 다시 불러와도 남는다). 그때 또 얹으면 문지기가 둘이
  // 되어, "나가기"가 한 칸만 물러나 게임에 그대로 남는다 — 이미 얹혀 있으면 그대로 쓴다.
  let armed = !!(history.state && (history.state as Record<string, unknown>)[GUARD]);
  let open: HTMLDialogElement | null = null;
  /** "나가기"로 떠나는 중. 그 사이의 기록 이동은 묻지 않는다. */
  let leavingNow = false;

  const onGuard = (): boolean => !!(history.state && (history.state as Record<string, unknown>)[GUARD]);
  const arm = (): void => {
    // 이미 문지기 위에 서 있으면 또 얹지 않는다(다시 불러온 뒤, 창이 뜬 채 앞으로가기로 돌아온 뒤) — 둘이 되면 "나가기"가
    // 한 칸만 물러나 게임에 남는다.
    if (onGuard()) armed = true;
    if (armed) return;
    try {
      history.pushState({ [GUARD]: true }, '');
      armed = true;
    } catch {
      // 기록을 못 쓰는 창(일부 내장 브라우저)이면 묻지 못한다 — 예전처럼 그냥 나간다.
    }
  };
  const first = (): void => {
    // 창이 떠 있을 때의 손짓(단추 누르기)은 얹지 않는다 — 다시 불러온 뒤 첫 손짓이 "나가기"면, 여기서 문지기를 다시 얹어
    // 나가기가 그것만 걷고 창이 또 떴다(리뷰가 재현했다). 머물면 창이 닫히며 얹는다.
    if (!open) arm();
    window.removeEventListener('pointerdown', first, true);
    window.removeEventListener('keydown', first, true);
  };
  const watchFirst = (): void => {
    window.addEventListener('pointerdown', first, true);
    window.addEventListener('keydown', first, true);
  };
  watchFirst();

  const ask = (): void => {
    if (open) return;
    const dialog = el('dialog', { class: 'sheet leave-dialog', 'aria-labelledby': 'leave-title' }) as HTMLDialogElement;
    open = dialog;
    let leaving = false;
    const stay = button('계속하기', () => dialog.close(), 'primary');
    const leave = button(
      '나가기',
      () => {
        leaving = true;
        dialog.close();
      },
      'mini',
    );
    dialog.append(
      el(
        'div',
        { class: 'sheet-in' },
        el('h3', { id: 'leave-title' }, '정말 게임을 나갈까?'),
        el('p', { class: 'dim' }, describe(hooks.status())),
        el('div', { class: 'sheet-actions' }, leave, stay),
      ),
    );
    // 창 안의 누르기·키는 **창에서 멈춘다.** 뒤 화면의 처리기는 문서·창에 붙어 있어(전투 입력 `InputState`, 부대 고르기를
    // 끝내는 `BattleControls`, 다시 보기를 닫는 `ReplayView`) 모달이어도 그대로 받는다. 막지 않았더니 창에서 누른 R·V가
    // 판이 다시 돌 때 후퇴·함성으로 실렸고, Space는 단추를 누르지 못했고, 계속하기를 누르면 부대 고르기가 끝나 판이
    // 돌았다(리뷰와 QA가 따로 잡았다). 그 처리기들은 모두 올라오는 길(bubble)에서 받으니 여기서 끊으면 닿지 않는다.
    for (const type of ['keydown', 'keypress', 'pointerdown', 'pointerup', 'pointermove', 'pointercancel', 'mousedown', 'mouseup', 'touchstart', 'touchmove', 'touchend', 'wheel', 'contextmenu']) {
      dialog.addEventListener(type, (e) => e.stopPropagation());
    }
    // **키를 놓는 것(keyup)은 끊지 않는다.** 놓기는 눌린 것을 풀 뿐 명령을 만들지 않는다(`InputState`의 keyup). 함께
    // 끊었더니, W를 누른 채 뒤로가기를 눌러 창 안에서 손을 떼면 판이 다시 돌 때 장수가 혼자 계속 걸었다(리뷰·QA).
    // 바깥·Esc로 닫으면 머문다 — 잘못 누른 뒤로가기를 되돌리는 쪽이 기본이다.
    dialog.addEventListener('click', (e) => {
      e.stopPropagation();
      if (e.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.remove();
      open = null;
      hooks.hold(false);
      if (leaving) {
        // 문지기는 이미 걷혔다. 한 번 더 뒤로 가면 들어오기 전 페이지다. 앞 페이지가 없는 창(새 탭으로 바로 연 것)에서는
        // 아무 일도 일어나지 않는다 — 그때는 다음 뒤로가기도 다시 묻지 않게 문지기를 얹지 않고 둔다.
        // 창이 뜬 채 앞으로가기로 문지기 위에 돌아와 있었다면 두 칸이다.
        leavingNow = true;
        armed = false;
        history.go(onGuard() ? -2 : -1);
        return;
      }
      arm();
    });
    hooks.hold(true);
    hooks.host().append(dialog);
    dialog.showModal();
    stay.focus();
  };

  // 떠났다가 뒤로·앞으로가기로 되살아난 페이지(bfcache)는 떠나던 그 상태로 깨어난다 — 다시 묻게 되돌린다.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    leavingNow = false;
    armed = onGuard();
    // 문지기 없는 항목으로 되살아났으면(나가기로 떠났다가 앞으로가기) 다음 손짓에 다시 얹는다. 첫 손짓 처리기는 이미
    // 떼어 냈으니 다시 붙인다 — 처음에는 armed만 내려 두어, 돌아와 다시 하는 판에서는 영영 묻지 않았다(리뷰가 실제
    // bfcache 복귀로 재현했다). 같은 처리기라 두 번 붙지 않는다.
    if (!armed) watchFirst();
  });

  window.addEventListener('popstate', () => {
    // 문지기가 걷혔다. 창이 이미 떠 있는데 또 온 것은 다른 기록 이동이다 — 건드리지 않는다.
    if (leavingNow) return;
    if (!armed) {
      // 창이 뜬 채 앞으로가기로 문지기 위에 돌아왔다 — 얹힌 것으로 맞춰 둔다(머물 때 또 얹지 않게).
      if (onGuard()) armed = true;
      return;
    }
    // 얹힌 채로 문지기에 온 것(앞으로가기)은 걷힌 것이 아니다.
    if (onGuard()) return;
    armed = false;
    ask();
  });
}
